import {action, computed, makeObservable, observable, runInAction} from 'mobx';
import {RootStore} from './index';
import {BaseStore} from './base.store';
import {DeviceRow, DisplayStatus, InviteRequest} from '@src/dtos/device.dto';
import devicesService from '../../services/devices.service';
import {triggerFileDownload} from '@src/utils/activity-util';
import moment from 'moment';

export class DevicesStore extends BaseStore {

    public devices: DeviceRow[] = [];
    public isLoading: boolean = false;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            devices: observable,
            isLoading: observable,
            load: action,
            loadSilently: action,
            create: action,
            edit: action,
            revoke: action,
            resend: action,
            downloadVpnUsage: action,
            inviteSentCount: computed,
            protectedCount: computed,
            unprotectedCount: computed,
            totalCount: computed,
        });
    }

    private get customerId(): number {
        return this.rootStore.customerStore.customerId;
    }

    private get serviceId(): number {
        return this.rootStore.customerStore.serviceId;
    }

    public async load() {
        await this.fetchDevices(true);
    }

    public async loadSilently() {
        await this.fetchDevices(false);
    }

    public async create(req: InviteRequest) {
        await devicesService.createInvite(this.customerId, this.serviceId, req);
        await this.load();
    }

    public async edit(inviteId: string, req: InviteRequest) {
        await devicesService.editInvite(this.customerId, this.serviceId, inviteId, req);
        await this.load();
    }

    public async revoke(inviteId: string) {
        this.isLoading = true;
        try {
            await devicesService.revokeInvite(this.customerId, this.serviceId, inviteId);
            await this.load();
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    public async resend(inviteId: string) {
        await devicesService.resendInvite(this.customerId, this.serviceId, inviteId);
        await this.load();
    }

    public async downloadVpnUsage(row: DeviceRow) {
        if (!row.deviceId || !row.vpnUsage) {
            throw new Error('VPN usage download requires a device and usage data');
        }

        const blob = await devicesService.exportVpnUsage(
            this.customerId,
            this.serviceId,
            row.deviceId,
            row.vpnUsage.windowStart,
            row.vpnUsage.windowEnd
        );
        const phoneNumber = row.phoneNumber.replace(/\D/g, '');
        const localTimestamp = moment().format('YYYYMMDD_HHmmss');
        triggerFileDownload(blob, `${phoneNumber}_${localTimestamp}.csv`);
    }

    get inviteSentCount(): number {
        return this.devices.filter(d =>
            d.displayStatus === DisplayStatus.PENDING ||
            d.displayStatus === DisplayStatus.SENT
        ).length;
    }

    get protectedCount(): number {
        return this.devices.filter(d => d.displayStatus === DisplayStatus.ACTIVE_VPN_ON).length;
    }

    get unprotectedCount(): number {
        return this.devices.filter(d =>
            d.displayStatus === DisplayStatus.ACTIVE_VPN_OFF ||
            d.displayStatus === DisplayStatus.REVOKED
        ).length;
    }

    get totalCount(): number {
        return this.devices.length;
    }

    public reset() {
        this.devices = [];
        this.isLoading = false;
    }

    private async fetchDevices(withLoading: boolean) {
        if (withLoading) {
            this.isLoading = true;
        }

        try {
            const res = await devicesService.listDevices(this.customerId, this.serviceId);
            runInAction(() => {
                this.devices = res ?? [];
            });
        } finally {
            if (withLoading) {
                runInAction(() => {
                    this.isLoading = false;
                });
            }
        }
    }
}
